import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => articles.find((a) => a.id === id), [id]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return article.relatedIds
      .map((rid) => articles.find((a) => a.id === rid))
      .filter(Boolean) as typeof articles;
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 px-4 md:px-6 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-foreground-400 text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接失效</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          author: { '@type': 'Person', name: article.author },
          datePublished: article.date,
          publisher: { '@type': 'Organization', name: '桃子视频', url: 'https://www.taozivideo.com/' },
          mainEntityOfPage: { '@type': 'WebPage', '@id': `https://www.taozivideo.com/article/${article.id}` },
        }}
      />
      <SchemaOrg
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.taozivideo.com/' },
            { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.taozivideo.com/category/${article.categorySlug}` },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center flex-wrap gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors cursor-pointer">{article.category}</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium line-clamp-1">{article.title}</span>
            </nav>

            {/* Article Header */}
            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-900 mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {article.views}阅读
                </span>
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700">
                  {article.category}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="relative overflow-hidden rounded-xl aspect-video mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Article Content */}
            <div className="prose-custom mb-10">
              {article.content.split('\n\n').map((paragraph, idx) => {
                const trimmed = paragraph.trim();
                if (!trimmed) return null;
                return (
                  <p key={idx} className="text-foreground-800 leading-relaxed mb-5 text-[15px]">
                    {trimmed}
                  </p>
                );
              })}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap items-center gap-2 mb-10 pt-6 border-t border-background-200/70">
              <span className="text-xs text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <section className="pt-6 border-t border-background-200/70">
                <h3 className="text-lg font-semibold text-foreground-900 mb-4">相关推荐</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group bg-background-100 rounded-lg overflow-hidden cursor-pointer"
                    >
                      <div className="relative overflow-hidden aspect-video">
                        <img
                          src={rel.image}
                          alt={rel.title}
                          title={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
                          {rel.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{rel.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}